/**
 * Expedition Loadout (Fun section). Pack a backpack from the shelf, then play three event cards.
 * Everything is plain buttons. The backpack is drawn as its 12 slots; tap a shelf item to pack it
 * and tap it in the backpack to take it out. The browser plays the trip; the server replays the
 * same loadout and choices to award medals and rewards. Supplies are free each attempt.
 */
import { LitElement, css, html, nothing, svg } from 'lit';
import {
  ADVENTURES,
  BACKPACK_SLOTS,
  HOME,
  MAX_COPIES,
  MAX_ENERGY,
  PIXEL_COLORS,
  SCENES,
  backpackSquares,
  blocked,
  expMedal,
  itemById,
  nextLevelId,
  rewardById,
  slotsUsed,
  start,
  step,
  tripReview,
  type Action,
  type Adventure,
  type ExpChoice,
  type ExpState,
  type ItemId,
  type Loadout,
  type Medal,
  type PixelArt,
} from '@ka/fun';
import { markWalkthroughSeen, shared, walkthroughSeen, type KaWalkthrough } from '@ka/ui';
import {
  WALK_ADVENTURE,
  WALK_ITEM,
  expeditionSteps,
  walkPackOk,
} from './expedition-walkthrough.ts';
import { currentFun, invitation, itemIcon, loadFun, setFun, type FunView } from './fun.ts';
import { currentKid, kidApi } from './kid.ts';
import { navigate } from './router.ts';

interface Draft {
  loadout: Loadout;
  actions: Action[];
  started: boolean;
}
const draftKey = (a: Adventure) => `ka.fun.expedition.${a.id}.v${a.version}`;
const empty = (): Draft => ({ loadout: {}, actions: [], started: false });

function loadDraft(a: Adventure): Draft {
  try {
    const d = JSON.parse(localStorage.getItem(draftKey(a)) ?? 'null') as Draft | null;
    // A draft that no longer replays (edited by hand, old rules) is simply dropped.
    if (d && Array.isArray(d.actions) && !('ok' in replayPartial(a, d))) return d;
  } catch {
    /* no draft */
  }
  return empty();
}
function saveDraft(a: Adventure, d: Draft) {
  try {
    localStorage.setItem(draftKey(a), JSON.stringify(d));
  } catch {
    /* private mode: the trip still works, it just is not kept */
  }
}

/** The state after the actions so far (the trip may not be finished). */
function replayPartial(a: Adventure, d: Draft) {
  let s = start(a, d.loadout);
  for (const act of d.actions) if (!('ok' in s)) s = step(a, s, act);
  return s;
}

function pixels(rows: PixelArt, size: number) {
  return svg`<svg width=${size} height=${size} viewBox="0 0 8 8" shape-rendering="crispEdges" aria-hidden="true">
    ${rows.flatMap((row, y) => [...row].map((ch, x) => (ch === '.' ? nothing : svg`<rect x=${x} y=${y} width="1" height="1" fill=${PIXEL_COLORS[ch]}/>`)))}
  </svg>`;
}
const icon = (id: ItemId, size = 32) => pixels(itemById(id)!.icon, size);
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const hasFood = (s: ExpState) =>
  (Object.keys(s.uses) as ItemId[]).some((id) => itemById(id)!.restores && s.uses[id]);

export class KaExpedition extends LitElement {
  static override properties = {
    adventureId: {},
    draft: { state: true },
    server: { state: true },
    view: { state: true },
    mark: { state: true },
    tip: { state: true },
    walk: { state: true },
    walkStep: { state: true },
  };
  declare adventureId: string;
  declare draft: Draft;
  declare server: {
    state: 'idle' | 'saving' | 'saved' | 'error';
    medal?: Medal | null;
    newRewards?: string[];
    message?: string;
  };
  declare view: FunView | null;
  /** The shelf item Fix it pointed at; cleared by the next packing change. */
  declare private mark: ItemId | null;
  /** What to do differently, carried into a retry with the same backpack. */
  declare private tip: string | null;
  /** The walkthrough is running on Glow Cave, on a backpack that is not saved as the draft. */
  declare private walk: boolean;
  declare private walkStep: string | null;
  private walkNext = false;
  /** Bumped for every new trip: a save reply for an earlier trip is ignored. */
  private attempt = 0;
  private walkChecked = false;
  private steps = expeditionSteps({
    goal: () => this.renderRoot.querySelector('.goal'),
    item: () => this.renderRoot.querySelector(`.tile[data-id="${WALK_ITEM}"]`),
    backpack: () => this.renderRoot.querySelector('.bag'),
    shelf: () => this.renderRoot.querySelector('.shelf'),
  });

  static override styles = [
    shared,
    css`
      :host {
        display: grid;
        gap: 1rem;
      }
      :host > * {
        min-width: 0;
      }
      .row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
      }
      .row[hidden] {
        display: none;
      }
      .top {
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem 1rem;
        align-items: center;
        margin: 0;
      }
      .top a {
        min-height: 44px;
        display: inline-flex;
        align-items: center;
      }
      .title {
        font: 600 1.25rem var(--ka-font-display);
      }
      .small {
        font-size: 0.9rem;
      }
      .play,
      .side,
      .board-col {
        display: grid;
        gap: 1rem;
        align-content: start;
        min-width: 0;
      }
      .panel {
        box-sizing: border-box;
        min-width: 0;
      }
      .goal,
      .status {
        display: grid;
        gap: 0.5rem;
      }
      .goal p,
      .goal ul,
      .status p,
      .board-col h2,
      .board-col p {
        margin: 0;
      }
      .goal h2 {
        font-size: 1rem;
        margin: 0;
      }
      .goal ul {
        padding-left: 1.2rem;
      }
      /* The backpack: 12 squares, 6 to a row, 44–56px. */
      .bag {
        display: grid;
        grid-template-columns: repeat(6, minmax(44px, 56px));
        gap: 3px;
        width: fit-content;
        max-width: 100%;
        padding: 3px;
        background: var(--ka-stone-900);
        border: 3px solid var(--ka-edge);
      }
      .sq {
        box-sizing: border-box;
        aspect-ratio: 1;
        display: grid;
        place-items: center;
        border: 2px dashed var(--ka-stone-600);
      }
      button.sq {
        all: unset;
        box-sizing: border-box;
        aspect-ratio: 1;
        display: grid;
        place-items: center;
        cursor: pointer;
        background: var(--ka-stone-600);
        border: 2px solid var(--ka-stone-500);
      }
      /* The rest of an item's slots: filled, no icon, joined to the square before. */
      button.sq.cont {
        border-left-style: dotted;
      }
      button.sq:focus-visible {
        outline: 3px solid var(--ka-focus);
        outline-offset: -3px;
      }
      button.sq:disabled {
        cursor: default;
      }
      .shelf {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        gap: 0.5rem;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 8.25rem), 1fr));
      }
      .tile {
        all: unset;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        min-height: 44px;
        cursor: pointer;
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 0.15rem 0.5rem;
        align-content: start;
        align-items: center;
        padding: 0.5rem;
        background: var(--ka-stone-800);
        border: 2px solid var(--ka-stone-600);
        color: var(--ka-parchment);
        font-size: 0.85rem;
        overflow-wrap: anywhere;
      }
      .tile svg {
        background: var(--ka-stone-900);
      }
      .tile > :nth-child(n + 3) {
        grid-column: 1 / -1;
      }
      .tile strong {
        font-size: 1rem;
      }
      .tile.packed {
        border-color: var(--ka-gold);
      }
      .tile.marked {
        outline: 3px solid var(--ka-focus);
        outline-offset: 2px;
      }
      .tile:focus-visible {
        outline: 3px solid var(--ka-focus);
      }
      .tile:disabled {
        cursor: default;
        opacity: 0.6;
      }
      .tile .note,
      .callout {
        grid-column: 1 / -1;
        justify-self: start;
        padding: 0.1em 0.4em;
        font: 600 0.8rem var(--ka-font-display);
      }
      .tile .note {
        background: var(--ka-stone-600);
      }
      .callout {
        background: var(--ka-focus);
        color: var(--ka-ink-on-gold);
        max-width: 100%;
        box-sizing: border-box;
      }
      .meter {
        display: flex;
        flex-wrap: wrap;
        gap: 3px;
      }
      .meter span {
        width: 14px;
        height: 14px;
        border: 2px solid var(--ka-edge);
        background: var(--ka-stone-900);
      }
      .meter span.full {
        background: var(--ka-foliage);
      }
      .stops {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }
      .stops li {
        padding: 0.1em 0.5em;
        border: 2px solid var(--ka-stone-600);
        font: 600 0.9rem var(--ka-font-display);
      }
      .stops li.now {
        border-color: var(--ka-gold);
        color: var(--ka-gold);
      }
      .pack {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }
      .pack > span {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0 0.4rem;
        min-height: 44px;
        box-sizing: border-box;
        background: var(--ka-stone-900);
        border: 2px solid var(--ka-stone-600);
      }
      .scene {
        display: flex;
        gap: 1rem;
        align-items: center;
      }
      .scene svg {
        flex: none;
        border: 3px solid var(--ka-edge);
      }
      .event {
        display: grid;
        gap: 0.75rem;
      }
      .event h2 {
        margin: 0 0 0.25rem;
      }
      .event p {
        margin: 0;
      }
      .choices {
        display: grid;
        gap: 0.5rem;
      }
      .choice {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 0.25rem 0.75rem;
        align-items: center;
        text-align: left;
        width: 100%;
        box-sizing: border-box;
        min-height: 56px;
        /* Easier reading than the pixel font for primary-school readers. */
        font: 600 1.05rem var(--ka-font-body);
      }
      .choice .icons {
        display: flex;
        gap: 2px;
        min-width: 32px;
      }
      .why {
        grid-column: 2;
        font-size: 0.85rem;
      }
      .said {
        border-left: 6px solid var(--ka-foliage);
        padding-left: 0.5rem;
      }
      .trail {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: 0.5rem;
      }
      .trail li {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 0.15rem 0.75rem;
        align-items: start;
        padding: 0.5rem 0.75rem;
        background: var(--ka-stone-800);
        border: 2px solid var(--ka-stone-600);
      }
      .trail li > :not(.num) {
        grid-column: 2;
      }
      .trail li.marked {
        outline: 3px solid var(--ka-focus);
        outline-offset: -3px;
      }
      .trail .medal {
        display: block;
      }
      .trail .callout {
        grid-column: 1 / -1;
      }
      .num {
        grid-row: span 2;
        font: 600 1.5rem var(--ka-font-display);
      }
      .medal {
        font-family: var(--ka-font-display);
        color: var(--ka-gold);
      }
      .dock,
      .sheet {
        display: grid;
        gap: 0.5rem;
        box-sizing: border-box;
      }
      .sheet h2,
      .sheet h3,
      .sheet p,
      .help {
        margin: 0;
      }
      .bar {
        display: flex;
        gap: 0.5rem;
        align-items: center;
      }
      .bar .btn {
        min-width: 44px;
        justify-content: center;
        white-space: nowrap;
      }
      .start {
        flex: 1;
      }
      .more {
        position: relative;
      }
      .more summary {
        list-style: none;
        box-sizing: border-box;
      }
      .more summary::-webkit-details-marker {
        display: none;
      }
      .more-body {
        position: absolute;
        right: 0;
        bottom: calc(100% + 0.5rem);
        z-index: 5;
        box-sizing: border-box;
        width: min(22rem, calc(100vw - 2rem));
        max-height: 70vh;
        overflow-y: auto;
        display: grid;
        gap: 0.75rem;
        box-shadow: var(--ka-lift);
      }
      .more-body p {
        margin: 0;
      }
      .more-body details summary {
        min-height: 44px;
        display: flex;
        align-items: center;
        cursor: pointer;
      }
      .levels {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 9rem), 1fr));
        gap: 0.5rem;
      }
      .levels a {
        display: grid;
        grid-template-columns: auto 1fr;
        column-gap: 0.5rem;
        row-gap: 0.25rem;
        align-items: baseline;
        padding: 0.75rem;
        min-height: 44px;
        height: 100%;
        box-sizing: border-box;
        background: var(--ka-stone-800);
        border: 2px solid var(--ka-edge);
        color: var(--ka-parchment);
        text-decoration: none;
      }
      .levels a > :last-child {
        grid-column: 1 / -1;
      }
      .levels a.next {
        outline: 3px solid var(--ka-focus);
      }
      /* Phones: the dock (or the result sheet) is pinned to the bottom. */
      @media (max-width: 899px) {
        .side {
          display: contents;
        }
        .dock,
        .sheet {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 4;
          padding: 0.75rem 1rem 1rem;
          background: var(--ka-stone-800);
          border: none;
          border-top: 3px solid var(--ka-edge);
          box-shadow: var(--ka-bevel);
        }
      }
      @media (max-width: 899px) {
        /* A long sheet (new rewards) scrolls inside itself; the dock is short and holds the ⋯ menu. */
        .sheet {
          max-height: 70vh;
          overflow-y: auto;
        }
      }
      @media (min-width: 900px) {
        .play {
          grid-template-columns: minmax(0, 1fr) 18rem;
          align-items: start;
        }
        .side {
          grid-column: 2;
        }
        .board-col {
          grid-column: 1;
          grid-row: 1;
        }
        .more-body {
          bottom: auto;
          top: calc(100% + 0.5rem);
        }
      }
    `,
  ];

  constructor() {
    super();
    this.adventureId = '';
    this.draft = empty();
    this.server = { state: 'idle' };
    this.view = currentFun();
    this.mark = null;
    this.tip = null;
    this.walk = false;
    this.walkStep = null;
  }

  private get adventure() {
    return ADVENTURES.find((a) => a.id === this.adventureId);
  }

  override connectedCallback() {
    super.connectedCallback();
    addEventListener('resize', this.measure);
    if (currentKid() && !this.view)
      void loadFun()
        .then((f) => (this.view = f))
        .catch(() => undefined);
  }
  override disconnectedCallback() {
    super.disconnectedCallback();
    removeEventListener('resize', this.measure);
    this.style.paddingBottom = '';
  }

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has('adventureId')) {
      this.attempt++;
      // Next adventure (or any link) swapped the screen under the focused control.
      if (this.shadowRoot?.activeElement) this.focusLater('.start', '.top a');
      if (this.adventure) this.draft = loadDraft(this.adventure);
      this.server = { state: 'idle' };
      this.mark = null;
      this.tip = null;
      // Leaving Glow Cave stops the walkthrough; arriving there from How to play starts it.
      this.walk = false;
      if (this.walkNext && this.adventureId === WALK_ADVENTURE) this.beginWalk();
      this.walkNext = false;
    }
  }

  override updated() {
    this.measure();
    // First time on this device: the walkthrough runs from the list or Glow Cave.
    if (!this.walkChecked && currentKid()) {
      this.walkChecked = true;
      if (
        !walkthroughSeen('expedition') &&
        (!this.adventureId || this.adventureId === WALK_ADVENTURE)
      )
        this.startWalk();
    }
  }

  /** Under 900px the dock or sheet is pinned: leave room for it at the bottom of the page. */
  private measure = () => {
    const dock = this.renderRoot.querySelector<HTMLElement>('.sheet, .dock');
    const pinned = dock && getComputedStyle(dock).position === 'fixed' ? dock.offsetHeight : 0;
    this.style.paddingBottom = pinned ? `${pinned}px` : '';
  };

  private set(d: Draft) {
    this.draft = d;
    if (!this.walk) saveDraft(this.adventure!, d);
  }

  /** Focuses the first of `sels` that exists after the next render. */
  private focusLater(...sels: string[]) {
    void this.updateComplete.then(() => {
      for (const sel of sels) {
        const el = this.renderRoot.querySelector<HTMLElement>(sel);
        if (el) return el.focus();
      }
    });
  }

  // ---------- packing ----------

  private pack(id: ItemId) {
    if (this.walk) {
      if (!walkPackOk(this.walkStep, id)) return;
      if (!this.draft.loadout[id]) this.draft = { ...this.draft, loadout: { [id]: 1 } };
      return this.coach?.complete('pack');
    }
    const n = (this.draft.loadout[id] ?? 0) + 1;
    const loadout = { ...this.draft.loadout, [id]: n };
    if (n > MAX_COPIES || slotsUsed(loadout) > BACKPACK_SLOTS) return;
    this.mark = null;
    this.set({ ...this.draft, loadout });
    // The tile is disabled once nothing more fits: keep focus on something useful.
    void this.updateComplete.then(() => {
      const t = this.renderRoot.querySelector<HTMLButtonElement>(`.tile[data-id="${id}"]`);
      if (t?.disabled) this.renderRoot.querySelector<HTMLElement>('.start')?.focus();
    });
  }

  private unpack(id: ItemId, index: number) {
    if (this.walk) return;
    const n = (this.draft.loadout[id] ?? 0) - 1;
    const loadout = { ...this.draft.loadout, [id]: n };
    if (n <= 0) delete loadout[id];
    this.mark = null;
    this.set({ ...this.draft, loadout });
    // The square may now be empty: focus the nearest packed item, or the item's shelf tile.
    void this.updateComplete.then(() => {
      const items = [...this.renderRoot.querySelectorAll<HTMLElement>('button.sq:not(.cont)')];
      const at = items.filter((b) => Number(b.dataset.i) <= index).at(-1) ?? items[0];
      (at ?? this.renderRoot.querySelector<HTMLElement>(`.tile[data-id="${id}"]`))?.focus();
    });
  }

  private emptyBag = () => {
    this.closeMenu();
    this.mark = null;
    this.set({ ...this.draft, loadout: {} });
    this.focusLater('.start');
  };

  private startTrip = () => {
    if (this.walk) return;
    this.set({ ...this.draft, started: true, actions: [] });
    this.focusLater('.event h2');
  };

  // ---------- playing ----------

  private act(a: Adventure, s: ExpState, action: Action) {
    const next = step(a, s, action);
    if ('ok' in next) return;
    this.set({ ...this.draft, actions: [...this.draft.actions, action] });
    if (next.ended) {
      this.tip = null;
      this.server = { state: 'idle' };
      this.focusLater('.sheet h2');
      if (next.ended === 'complete') void this.submit();
    } else if ('eat' in action) this.focusLater('.choice:not([disabled])');
    else this.focusLater('.event h2');
  }

  private submit = async () => {
    const a = this.adventure!;
    if (!currentKid()) return;
    const attempt = this.attempt;
    const retrying = this.server.state === 'error';
    this.server = { state: 'saving' };
    try {
      const s = await kidApi<{ medal: Medal | null; newRewards: string[]; fun: FunView }>(
        'POST',
        `/fun/expedition/${a.id}`,
        { version: a.version, loadout: this.draft.loadout, actions: this.draft.actions },
      );
      setFun(s.fun);
      this.view = s.fun;
      // A late reply for a trip the kid has left (another adventure or a new try) must not show.
      if (attempt !== this.attempt) return;
      this.server = { state: 'saved', medal: s.medal, newRewards: s.newRewards };
      // The error's Try again button is gone: keep focus in the sheet.
      if (retrying) this.focusLater('.sheet h2');
    } catch (e) {
      if (attempt !== this.attempt) return;
      // API errors carry a status and a kid-friendly message; network errors ("Failed to fetch") don't.
      const api = e instanceof Error && 'status' in e;
      this.server = {
        state: 'error',
        message: api ? e.message : 'Could not save. Check the connection and try again.',
      };
    }
  };

  /** Same backpack, new trip. */
  private retry(tip: string | null = null) {
    this.tip = tip;
    this.attempt++;
    this.server = { state: 'idle' };
    this.set({ ...this.draft, actions: [] });
    this.focusLater('.event h2');
  }

  /** Back to packing; with `mark`, that shelf tile is marked (or, with no room, the backpack). */
  private repack(mark: ItemId | null = null, room = true) {
    this.tip = null;
    this.attempt++;
    this.mark = mark;
    this.server = { state: 'idle' };
    this.set({ ...this.draft, actions: [], started: false });
    void this.updateComplete.then(() => {
      const q = (sel: string) => this.renderRoot.querySelector<HTMLElement>(sel);
      const t = mark && q(`.tile[data-id="${mark}"]`);
      if (!t) return q('.start')?.focus();
      t.scrollIntoView({ block: 'center' });
      // The marked tile is disabled while the backpack is full: start where room is made.
      (room ? t : (q('button.sq:not(.cont)') ?? q('.start')))?.focus();
    });
  }

  private closeMenu() {
    const m = this.renderRoot.querySelector<HTMLDetailsElement>('details.more');
    if (m) m.open = false;
  }

  // ---------- views ----------

  private list() {
    const solved = this.view?.progress.solved.expedition ?? {};
    const next = nextLevelId(ADVENTURES, solved);
    const done = ADVENTURES.filter((a) => solved[a.id]).length;
    const gold = ADVENTURES.filter((a) => solved[a.id] === 'gold').length;
    return html`<p class="top"><a href="/fun">← Fun</a></p>
      <section class="panel">
        <h1>Expedition Loadout</h1>
        <p>Read what's ahead. Pack your backpack. Then pick a plan at every stop.</p>
        <p class="small muted">
          Game rules of our own, inspired by Minecraft adventures. Supplies are free every time.
        </p>
      </section>
      <ol class="levels">
        ${ADVENTURES.map((a, i) => {
          const status =
            [
              solved[a.id] === 'gold' ? '★ Gold' : solved[a.id] ? '✓ Solved' : '',
              a.id === next ? 'Play next →' : '',
            ]
              .filter(Boolean)
              .join(' · ') || 'New';
          return html`<li>
            <a class=${a.id === next ? 'next' : ''} href="/fun/expedition?adventure=${a.id}">
              <span class="num">${i + 1}</span>
              <span>${a.title}</span>
              <span class=${a.id === next || solved[a.id] ? 'medal' : 'muted'}>${status}</span>
            </a>
          </li>`;
        })}
      </ol>
      <p class="row">
        <button class="btn" @click=${this.startWalk}>How to play</button>
        <span class="small muted">${done} / ${ADVENTURES.length} · ★ ${gold}</span>
      </p>`;
  }

  private topBar(a: Adventure) {
    return html`<p class="top">
      <a href="/fun/expedition">← Adventures</a>
      <span class="title">${ADVENTURES.indexOf(a) + 1} · ${a.title}</span>
    </p>`;
  }

  private packing(a: Adventure) {
    const used = slotsUsed(this.draft.loadout);
    const squares = backpackSquares(a, this.draft.loadout);
    return html`${this.topBar(a)}
      <div class="play">
        <div class="side">
          <section class="panel goal">
            <p>${a.objective}</p>
            <h2>What's ahead</h2>
            <ul>
              ${a.forecast.map((f) => html`<li>${f}</li>`)}
            </ul>
            <p class="small muted">Gold medal: the best plan at every stop.</p>
          </section>
          <div class="dock">
            <p class="small muted help">
              Tap an item to pack it. Tap it in your backpack to take it out.
            </p>
            <div class="bar">
              <button class="btn primary start" ?disabled=${this.walk} @click=${this.startTrip}>
                Start
              </button>
              ${this.menu(
                html`<button class="btn" ?disabled=${!used} @click=${this.emptyBag}>
                  Empty backpack
                </button>`,
              )}
            </div>
          </div>
        </div>
        <div class="board-col">
          <section class="panel status">
            <h2>Your backpack</h2>
            <div
              class="bag"
              role="group"
              aria-label="Your backpack, ${used} of ${BACKPACK_SLOTS} slots used"
            >
              ${squares.map((sq, i) => {
                if (!sq) return html`<div class="sq" aria-hidden="true"></div>`;
                const item = itemById(sq.id)!;
                return html`<button
                  class="sq ${sq.first ? '' : 'cont'}"
                  data-i=${i}
                  tabindex=${sq.first ? 0 : -1}
                  aria-hidden=${sq.first ? 'false' : 'true'}
                  aria-label="${item.name}. Tap to take one out"
                  ?disabled=${this.walk}
                  @click=${() => this.unpack(sq.id, i)}
                >
                  ${sq.first ? icon(sq.id, 32) : nothing}
                </button>`;
              })}
            </div>
            <p class="small" aria-live="polite">${used} of ${BACKPACK_SLOTS} slots used</p>
          </section>
          <section class="panel status">
            <h2>Shelf</h2>
            <ul class="shelf">
              ${a.shelf.map((id) => this.tile(id, used))}
            </ul>
          </section>
        </div>
      </div>`;
  }

  private tile(id: ItemId, used: number) {
    const item = itemById(id)!;
    const n = this.draft.loadout[id] ?? 0;
    const full = n >= MAX_COPIES;
    const fits = !full && used + item.slots <= BACKPACK_SLOTS;
    const marked = this.mark === id;
    return html`<li>
      <button
        class="tile ${n ? 'packed' : ''} ${marked ? 'marked' : ''}"
        data-id=${id}
        ?disabled=${!fits}
        aria-label="Pack ${item.name}. ${plural(item.slots, 'slot')}, ${plural(item.uses, 'use')}. ${item.text}${n ? ` ${n} packed.` : ''}${marked ? (fits ? ' Pack this one.' : ' Make room: take something out first.') : ''}${fits ? '' : full ? ' That is the most you can take.' : ' No room left.'}"
        @click=${() => this.pack(id)}
      >
        ${pixels(item.icon, 32)}
        <strong>${item.name}${n ? html` <span class="medal">×${n}</span>` : nothing}</strong>
        <span class="muted">${plural(item.slots, 'slot')} · ${plural(item.uses, 'use')}</span>
        <span>${item.text}</span>
        ${
          marked
            ? html`<span class="callout"
                >${fits ? 'Pack this' : 'Make room: take something out first'}</span
              >`
            : nothing
        }
        ${fits || marked ? nothing : html`<span class="note">${full ? 'That’s the most' : 'No room'}</span>`}
      </button>
    </li>`;
  }

  private menu(extra: unknown) {
    return html`<details class="more" ?hidden=${this.walk}>
      <summary class="btn" aria-label="More">⋯</summary>
      <div class="panel more-body">
        <p class="row">
          ${extra}
          <button class="btn" @click=${this.startWalk}>How to play</button>
        </p>
        ${this.rules()}
      </div>
    </details>`;
  }

  private backpack(a: Adventure, s: ExpState) {
    const ids = (Object.keys(s.uses) as ItemId[]).filter((id) => s.uses[id]);
    return html`<div class="pack" role="group" aria-label="What's left in your backpack">
      ${ids.length ? nothing : html`<span class="small">Nothing left in your backpack.</span>`}
      ${ids.map((id) => {
        const item = itemById(id)!;
        return html`<span>
          ${icon(id, 24)} ${item.name} ×${s.uses[id]}
          ${
            item.restores
              ? html`<button
                  class="btn"
                  ?disabled=${s.energy >= MAX_ENERGY}
                  @click=${() => this.act(a, s, { eat: id })}
                >
                  Eat (+${item.restores})
                </button>`
              : nothing
          }
        </span>`;
      })}
    </div>`;
  }

  private why(s: ExpState, c: ExpChoice) {
    const b = blocked(s, c);
    if (!b) return '';
    if (b.missing.length)
      return `You need ${b.missing.map((id) => `${c.use[id]} × ${itemById(id)!.name}`).join(' and ')}.`;
    return hasFood(s) ? 'Too tired. Eat something first.' : 'Too tired for this one.';
  }

  private choiceButton(a: Adventure, s: ExpState, c: ExpChoice) {
    const why = this.why(s, c);
    const uses = (Object.entries(c.use) as [ItemId, number][]).map(
      ([id, n]) => `${n} × ${itemById(id)!.name}`,
    );
    const cost = c.energy < 0 ? `Energy −${-c.energy}` : c.energy > 0 ? `Energy +${c.energy}` : '';
    return html`<button
      class="btn choice"
      ?disabled=${!!why}
      @click=${() => this.act(a, s, { choose: c.id })}
    >
      <span class="icons">
        ${Object.keys(c.use).map((id) => icon(id as ItemId, 32))}
        ${Object.keys(c.use).length ? nothing : pixels(SCENES.walk!, 32)}
      </span>
      <span>${c.label}</span>
      <span class="why">${why || [...uses, cost].filter(Boolean).join(' · ') || 'Free'}</span>
    </button>`;
  }

  private meter(n: number) {
    return html`<div class="meter" role="img" aria-label="Energy ${n} of ${MAX_ENERGY}">
      ${Array.from({ length: MAX_ENERGY }, (_, i) => html`<span class=${i < n ? 'full' : ''}></span>`)}
    </div>`;
  }

  private trip(a: Adventure, s: ExpState) {
    const ev = a.events[s.event]!;
    // Food could still unlock a choice, so only "stuck" when there is nothing left to eat.
    const stuck = ev.choices.every((c) => blocked(s, c)) && !hasFood(s);
    const last = s.log.at(-1);
    const lastChoice = last
      ? a.events.find((e) => e.id === last.event)!.choices.find((c) => c.id === last.choice)
      : undefined;
    return html`${this.topBar(a)}
      <div class="play">
        <div class="side">
          <section class="panel status">
            <ol class="stops" aria-label="Stop ${s.event + 1} of ${a.events.length}">
              ${a.events.map(
                (_, i) =>
                  html`<li class=${i === s.event ? 'now' : i < s.event ? '' : 'muted'}>
                    ${i + 1}${i < s.event ? ' ✓' : i === s.event ? ' · now' : ''}
                  </li>`,
              )}
            </ol>
            <p class="row small">Energy ${s.energy} of ${MAX_ENERGY} ${this.meter(s.energy)}</p>
            ${this.backpack(a, s)}
            ${this.tip ? html`<p class="small"><strong>Tip:</strong> ${this.tip}</p>` : nothing}
          </section>
        </div>
        <div class="board-col">
          <section class="panel event">
            <div aria-live="polite">
              ${lastChoice ? html`<p class="said">${lastChoice.result}</p>` : nothing}
            </div>
            <div class="scene">
              ${pixels(SCENES[ev.scene]!, 80)}
              <div>
                <h2 tabindex="-1">${ev.title}</h2>
                <p>${ev.text}</p>
              </div>
            </div>
            <div class="choices" role="group" aria-label="What will you do?">
              ${ev.choices.map((c) => this.choiceButton(a, s, c))}
            </div>
            ${
              stuck
                ? html`<p>
                    <strong>None of these work right now.</strong> Head home, pack again, and have
                    another go!
                  </p>`
                : nothing
            }
            <p class="row">
              <button
                class="btn ${stuck ? 'primary' : ''}"
                @click=${() => this.act(a, s, { choose: HOME.id })}
              >
                ${HOME.label}
              </button>
            </p>
          </section>
        </div>
      </div>`;
  }

  private end(a: Adventure, s: ExpState) {
    const review = tripReview(a, this.draft.loadout, this.draft.actions);
    const medal = expMedal(a, s);
    const next = ADVENTURES[ADVENTURES.indexOf(a) + 1];
    const rewards = (this.server.newRewards ?? []).map(rewardById).filter(Boolean);
    const homeAt = review.stops.findIndex((x) => x.status === 'home');
    const buttons =
      medal === 'gold'
        ? html`${
              next
                ? html`<button
                    class="btn green"
                    @click=${() => navigate(`/fun/expedition?adventure=${next.id}`)}
                  >
                    Next adventure
                  </button>`
                : html`<a class="btn green" href="/fun/expedition">All adventures</a>`
            }
            <button class="btn" @click=${() => this.retry()}>Try again</button>
            <button class="btn" @click=${() => this.repack()}>Repack</button>`
        : review.item
          ? html`<button class="btn green" @click=${() => this.repack(review.item, review.room)}>
                Fix it
              </button>
              <button class="btn" @click=${() => this.retry()}>Try again</button>`
          : html`<button class="btn green" @click=${() => this.retry(review.say)}>Fix it</button>
              <button class="btn" @click=${() => this.repack()}>Repack</button>`;
    return html`${this.topBar(a)}
      <div class="play">
        <div class="side">
          <section class="panel sheet">
            <h2 tabindex="-1">
              ${medal === 'gold' ? '★ Gold medal!' : medal ? 'Trip done!' : 'Home safe'}
            </h2>
            <p>
              ${
                medal
                  ? `Score ${s.score} of ${a.events.length * 3}.${medal === 'gold' ? '' : ' Gold: the best plan at every stop.'}`
                  : `You headed home at stop ${homeAt + 1}.`
              }
              ${review.marked !== null ? 'Look at the marked stop.' : ''}
            </p>
            <div aria-live="polite">
              ${this.server.state === 'saving' ? html`<p class="small muted">Saving…</p>` : nothing}
              ${this.server.state === 'error' ? html`<p class="small err">${this.server.message} <button class="btn" @click=${this.submit}>Try again</button></p>` : nothing}
            </div>
            ${
              rewards.length
                ? html`<h3>New for your character</h3>
                    <p class="row">
                      ${rewards.map((x) => html`<span class="row">${itemIcon(x!)} <strong>${x!.name}</strong></span>`)}
                    </p>
                    <p><a class="btn" href="/fun/character">Equip it</a></p>`
                : nothing
            }
            <p class="row">${buttons}</p>
          </section>
        </div>
        <div class="board-col">
          <section class="panel status">
            <h2>Your trip</h2>
            <ol class="trail">
              ${a.events.map((ev, i) => {
                const st = review.stops[i]!;
                const marked = review.marked === i;
                return html`<li class=${marked ? 'marked' : ''}>
                  <span class="num">${i + 1}</span>
                  <strong>${ev.title}</strong>
                  <span class="small">
                    ${
                      st.status === 'not-reached'
                        ? html`<span class="muted">Not reached</span>`
                        : st.status === 'home'
                          ? 'You headed home here.'
                          : st.choice!.result
                    }
                    ${st.status === 'best' ? html`<span class="medal">✓ Best plan</span>` : nothing}
                  </span>
                  ${marked ? html`<span class="callout">${review.say}</span>` : nothing}
                </li>`;
              })}
            </ol>
          </section>
        </div>
      </div>`;
  }

  private rules() {
    return html`<details>
      <summary>How it works</summary>
      <ul class="small">
        <li>
          Your backpack holds ${BACKPACK_SLOTS} slots. Each item says how many slots it takes and
          how many times you can use it.
        </li>
        <li>What's ahead tells you everything you will meet on the way.</li>
        <li>At each stop, pick what to do. Some choices use up items or energy.</li>
        <li>Food gives you energy back. Eat it any time on the trip.</li>
        <li>
          No right item? There is always a slower safe way, or you can head home and try again.
        </li>
        <li>Supplies are free every time and stay in this game. They are not prizes.</li>
      </ul>
    </details>`;
  }

  // ---------- walkthrough ----------

  private get coach() {
    return this.renderRoot.querySelector<KaWalkthrough>('ka-walkthrough');
  }

  /** Runs the walkthrough on Glow Cave on an empty backpack; the kid's own draft is kept. */
  private startWalk = () => {
    markWalkthroughSeen('expedition');
    this.closeMenu();
    if (this.adventureId === WALK_ADVENTURE) return this.beginWalk();
    this.walkNext = true;
    navigate(`/fun/expedition?adventure=${WALK_ADVENTURE}`);
  };

  private beginWalk() {
    this.draft = empty();
    this.mark = null;
    this.tip = null;
    this.server = { state: 'idle' };
    this.walkStep = null;
    this.walk = true;
  }

  private onWalkEnd() {
    this.walk = false;
    this.walkStep = null;
    const a = this.adventure;
    if (!a) return;
    const kept = loadDraft(a);
    // The kid's own backpack or trip comes back; with none, the walkthrough's backpack is kept.
    if (kept.started || slotsUsed(kept.loadout)) this.draft = kept;
    else saveDraft(a, this.draft);
  }

  override render() {
    if (!currentKid()) return invitation();
    const a = this.adventure;
    let body;
    if (!a) body = this.list();
    else {
      const s = this.draft.started ? replayPartial(a, this.draft) : null;
      body = !s || 'ok' in s ? this.packing(a) : s.ended ? this.end(a, s) : this.trip(a, s);
    }
    return html`${body}
      <ka-walkthrough
        .steps=${this.steps}
        ?open=${this.walk}
        @ka-walkthrough-step=${(e: CustomEvent<{ id: string }>) => (this.walkStep = e.detail.id)}
        @ka-walkthrough-end=${this.onWalkEnd}
      ></ka-walkthrough>`;
  }
}
customElements.define('ka-expedition', KaExpedition);
